import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SUGGESTIONS = ["How do I buy unlisted shares here?", "What is the minimum order?", "What happens after I pay by UPI?", "Which companies are on the board?"];

const getSession = () => {
  let s = localStorage.getItem("assistant_session");
  if (!s) { s = "s" + crypto.randomUUID().replace(/-/g, "").slice(0, 30); localStorage.setItem("assistant_session", s); }
  return s;
};

const renderText = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong> : part));

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const session = useRef(getSession());

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/api/assistant/history/${session.current}`).then((r) => r.json()).then((d) => setMessages((prev) => (prev.length ? prev : d.messages || []))).catch(() => {}).finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: session.current, message: q }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Assistant unavailable");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.delta }; return c; });
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: typeof e?.message === "string" ? e.message : "Something went wrong. Please try again." }; return c; });
    } finally { setBusy(false); }
  };

  const clear = async () => {
    await fetch(`${API}/api/assistant/history/${session.current}`, { method: "DELETE" }).catch(() => {});
    setMessages([]);
  };

  return (
    <>
      <button data-testid="assistant-open-button" onClick={() => setOpen(!open)} aria-label="Ask the desk assistant" className="fixed bottom-24 right-6 z-50 flex h-14 items-center gap-2 rounded-full border border-[#6D28D9]/40 bg-[#6D28D9] px-5 text-white shadow-[0_8px_30px_rgba(109,40,217,0.35)] transition-transform duration-300 hover:scale-105 md:bottom-6">
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}<span className="hidden font-mono2 text-[11px] uppercase tracking-[0.2em] sm:inline">{open ? "Close" : "Ask AI"}</span>
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-40 right-4 z-50 flex h-[min(560px,calc(100vh-200px))] w-[calc(100vw-2rem)] max-w-[400px] flex-col border border-slate-200 bg-white shadow-[0_24px_80px_-20px_rgba(15,23,42,0.35)] md:bottom-24 md:right-6">
          <div className="flex items-center justify-between border-b border-slate-200 bg-[#F6F4FD] px-4 py-3">
            <div>
              <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]"><Sparkles className="h-3.5 w-3.5" /> Desk Assistant</p>
              <p className="text-[11px] text-slate-500">Powered by Claude · information, not advice</p>
            </div>
            <button data-testid="assistant-clear-button" onClick={clear} aria-label="Clear conversation" className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>

          <div data-testid="assistant-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-slate-600">Hi — ask me anything about unlisted shares, the buying process, KYC or the companies on our board.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)} className="border border-slate-200 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:border-[#6D28D9]/50 hover:text-[#6D28D9]">{s}</button>)}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#6D28D9] text-white" : "bg-[#F6F4FD] text-slate-800"}`}>
                  {m.content ? renderText(m.content) : <Loader2 className="h-4 w-4 animate-spin text-[#6D28D9]" />}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-slate-200 p-3">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={1500} placeholder="Ask about a share, KYC, payments…" className="flex-1 border border-slate-200 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-[#6D28D9]/60" />
            <button data-testid="assistant-send-button" type="submit" disabled={busy || !input.trim()} aria-label="Send" className="flex h-10 w-10 items-center justify-center bg-[#6D28D9] text-white hover:bg-[#5B21B6] disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
          </form>
          <p className="border-t border-slate-100 px-4 py-2 text-[10px] leading-snug text-slate-400">Not a SEBI-registered adviser. Unlisted shares are illiquid and high-risk. Never share passwords or OTPs.</p>
        </div>
      )}
    </>
  );
};
